import { Formik } from 'formik';
import { useContext, useState, useEffect } from 'react';

import { CustomerEditContext } from './CustomerEdit';

import { CloseButton } from '@components/ActionButtons';
import { Box } from '@components/Box';
import {
  FormSectionContainer,
  FormButtonsContainer,
} from '@components/Container';
import {
  FormikInput,
  CancelButton,
  SubmitButton,
  FormikCheckbox,
} from '@components/Form';
import { ActionableSelect } from '@components/Form/ActionableSelect';
import { OkButton } from '@components/Form/OkButton';
import { Loading } from '@components/Loading';
import { Modal } from '@components/Modal';
import {
  BillingAddressViewModel,
  billingAddressDefaultValue,
  billingAddressValidation,
} from '@models/BillingAddress';
import { FormikSelectOption } from '@models/common/FormikSelectOption';
import { BillingAddressService } from '@services/BillingAddress';
import { CustomerService } from '@services/Customer';

export const CustomerEditBillingAddressSelect = () => {
  const { customer, setCustomer } = useContext(CustomerEditContext);
  const [
    isCustomerBillingAddressesLoading,
    setIsCustomerBillingAddressesLoading,
  ] = useState(true);
  const customerBillingAddresses = customer.billingAddresses || [];
  const [
    customerBillingAddressSelectOptions,
    setCustomerBillingAddressSelectOptions,
  ] = useState<FormikSelectOption[]>([]);
  const [isShowAddBillingAddress, setIsShowAddBillingAddress] = useState(false);
  const [isShowEditBillingAddress, setIsShowEditBillingAddress] =
    useState(false);
  const [isShowDeleteBillingAddress, setIsShowDeleteBillingAddress] =
    useState(false);
  const [billingAddressToEdit, setBillingAddressToEdit] =
    useState<BillingAddressViewModel | null>();
  const [isLoading, setIsLoading] = useState(true);

  const loadBillingAddresses = () => {
    CustomerService.getCustomerBillingAddresses(Number(customer.id)).then(
      (result) => {
        if (result.isSuccess && 'data' in result) {
          setCustomer((prevState) => ({
            ...prevState,
            billingAddresses: result.data,
          }));
        }

        setIsCustomerBillingAddressesLoading(false);
      }
    );
  };

  const loadBillingAddressOptions = () => {
    const customerBillingAddressSelection = [{ value: '0', text: '' }].concat(
      customerBillingAddresses.map((row) => ({
        value: `${row.id}`,
        text: `${row.address1} ${row.address2}, ${row.city}, ${row.state} ${row.postalCode}, ${row.country}`,
      }))
    );

    setCustomerBillingAddressSelectOptions(customerBillingAddressSelection);
  };

  useEffect(() => {
    loadBillingAddressOptions();
  }, [customer.billingAddresses]);

  useEffect(() => {
    if (!isCustomerBillingAddressesLoading) {
      setIsLoading(false);
    }
  }, [isCustomerBillingAddressesLoading]);

  useEffect(() => {
    loadBillingAddresses();
  }, [customer.id]);

  if (isLoading) {
    return <Loading />;
  } else {
    return (
      <>
        <ActionableSelect
          label="Billing Address"
          value={`${customer.billingID}`}
          selection={customerBillingAddressSelectOptions}
          onAddAction={() => {
            setIsShowAddBillingAddress(true);
          }}
          onEditAction={() => {
            if (customer.billingID == undefined || customer.billingID == 0)
              return;

            const selectedBillingAddress = customerBillingAddresses.find(
              (row) => row.id == customer.billingID
            );

            if (selectedBillingAddress == undefined) return;

            setBillingAddressToEdit(selectedBillingAddress);
            setIsShowEditBillingAddress(true);
          }}
          onDeleteAction={() => setIsShowDeleteBillingAddress(true)}
          onDuplicateAction={() => {
            const selectedBillingAddress = customerBillingAddresses.find(
              (row) => row.id == customer.billingID
            );

            if (selectedBillingAddress == undefined) return;

            selectedBillingAddress.id = 0;
            BillingAddressService.create(selectedBillingAddress).then(
              (result) => {
                if (result.isSuccess && 'data' in result) {
                  const newBillingAddress = {
                    ...selectedBillingAddress,
                    id: result.data.id,
                  };

                  const updatedBillingAddresses =
                    customerBillingAddresses.concat(newBillingAddress);

                  setCustomer({
                    ...customer,
                    billingAddresses: updatedBillingAddresses,
                  });

                  setBillingAddressToEdit(newBillingAddress);
                  setIsShowEditBillingAddress(true);
                }
              }
            );
          }}
          onChange={(e) => {
            const updateCustomer = {
              ...customer,
              billingID: Number(e.target.value),
            };
            setCustomer(updateCustomer);
          }}
        />

        {isShowDeleteBillingAddress && (
          <>
            <Modal show={true}>
              <CloseButton
                onClick={() => setIsShowDeleteBillingAddress(false)}
              />
              <Box size="medium">
                <>
                  <div className="px-4 pb-2">
                    <span>
                      Are you sure you want to delete this record from your
                      Billing Address lists?
                    </span>
                  </div>
                  <FormButtonsContainer>
                    <div className="space-x-2">
                      <CancelButton
                        onClick={() => setIsShowDeleteBillingAddress(false)}
                      />
                      <OkButton
                        onClick={async () => {
                          const deleteBillingAddresses =
                            customerBillingAddresses.find(
                              (row) => row.id == customer.billingID
                            );

                          if (deleteBillingAddresses == undefined) return;

                          const result = await BillingAddressService.delete(
                            deleteBillingAddresses.id
                          );
                          if (result.isSuccess) {
                            const updatedBillingAddresses =
                              customerBillingAddresses.filter(
                                (row) => row.id !== customer.billingID
                              );

                            const nextBillingId =
                              updatedBillingAddresses.length > 0
                                ? updatedBillingAddresses[0].id
                                : 0;

                            setCustomer({
                              ...customer,
                              billingID: nextBillingId,
                              billingAddresses: updatedBillingAddresses,
                            });
                            setIsShowDeleteBillingAddress(false);
                          } else {
                            console.error(
                              'Error: Failed to delete the record.',
                              result.errorMessages
                            );
                            alert(`Error: ${result.errorMessages}`);
                          }
                        }}
                      >
                        Delete
                      </OkButton>
                    </div>
                  </FormButtonsContainer>
                </>
              </Box>
            </Modal>
          </>
        )}

        {isShowAddBillingAddress && (
          <>
            <Modal show={true}>
              <CloseButton onClick={() => setIsShowAddBillingAddress(false)} />

              <Formik
                initialValues={billingAddressDefaultValue}
                validationSchema={billingAddressValidation}
                validateOnBlur={true}
                validateOnChange={true}
                onSubmit={(values, actions) => {
                  values.customerId = Number(customer.id);

                  BillingAddressService.create(values).then((result) => {
                    if (result.isSuccess && 'data' in result) {
                      values.id = result.data.id;
                      const updatedBillingAddresses =
                        customerBillingAddresses.concat(values);

                      let activeId = values.id;

                      if (!values.isActive) {
                        const firstActive = updatedBillingAddresses.find(
                          (item) => item.isActive
                        );
                        activeId = firstActive ? firstActive.uiid : 0;
                      }

                      setCustomer({
                        ...customer,
                        billingID: activeId,
                        billingAddresses: updatedBillingAddresses,
                      });

                      actions.resetForm();
                      setIsShowAddBillingAddress(false);
                    }
                  });
                }}
              >
                {(formikProps) => {
                  return (
                    <>
                      <form method="POST" onSubmit={formikProps.handleSubmit}>
                        <FormSectionContainer>
                          <FormikInput
                            label="Currency Code"
                            name="currencyCode"
                          />
                          <FormikInput label="Address 1" name="address1" />
                          <FormikInput label="Address 2" name="address2" />
                          <FormikInput label="City" name="city" />
                          <FormikInput label="State / Province" name="state" />
                          <FormikInput label="Country" name="country" />
                          <FormikInput label="Postal Code" name="postalCode" />
                          <FormikCheckbox label="Is Active" name="isActive" />
                        </FormSectionContainer>
                        <FormButtonsContainer>
                          <CancelButton
                            onClick={() => {
                              setIsShowAddBillingAddress(false);
                              formikProps.resetForm();
                            }}
                          />

                          <SubmitButton
                            label="Save"
                            disabled={
                              formikProps.isSubmitting || !formikProps.isValid
                            }
                          />
                        </FormButtonsContainer>
                      </form>
                    </>
                  );
                }}
              </Formik>
            </Modal>
          </>
        )}

        {isShowEditBillingAddress && billingAddressToEdit && (
          <>
            <Modal show={true}>
              <CloseButton onClick={() => setIsShowEditBillingAddress(false)} />

              <Formik
                initialValues={billingAddressToEdit}
                validationSchema={billingAddressValidation}
                validateOnBlur={true}
                validateOnChange={true}
                onSubmit={(values, actions) => {
                  BillingAddressService.update(values.id, values).then(
                    (result) => {
                      if (result.isSuccess) {
                        const updatedBillingAddresses =
                          customerBillingAddresses.map((row) =>
                            row.id === values.id ? values : row
                          );
                        let activeId = values.id;

                        if (!values.isActive) {
                          const firstActive = updatedBillingAddresses.find(
                            (item) => item.isActive
                          );
                          activeId = firstActive ? firstActive.id : 0;
                        }

                        setCustomer({
                          ...customer,
                          billingID: activeId,
                          billingAddresses: updatedBillingAddresses,
                        });

                        actions.resetForm();
                        setIsShowEditBillingAddress(false);
                      }
                    }
                  );
                }}
              >
                {(formikProps) => {
                  return (
                    <>
                      <form method="POST" onSubmit={formikProps.handleSubmit}>
                        <FormSectionContainer>
                          <FormikInput
                            label="Currency Code"
                            name="currencyCode"
                          />
                          <FormikInput label="Address 1" name="address1" />
                          <FormikInput label="Address 2" name="address2" />
                          <FormikInput label="City" name="city" />
                          <FormikInput label="State / Province" name="state" />
                          <FormikInput label="Country" name="country" />
                          <FormikInput label="Postal Code" name="postalCode" />
                          <FormikCheckbox label="Is Active" name="isActive" />
                        </FormSectionContainer>
                        <FormButtonsContainer>
                          <CancelButton
                            onClick={() => {
                              setIsShowEditBillingAddress(false);
                              formikProps.resetForm();
                            }}
                          />

                          <SubmitButton
                            label="Save"
                            disabled={
                              formikProps.isSubmitting || !formikProps.isValid
                            }
                          />
                        </FormButtonsContainer>
                      </form>
                    </>
                  );
                }}
              </Formik>
            </Modal>
          </>
        )}
      </>
    );
  }
};
